import React from 'react';
import { Badge } from '../../components/ui/badge';

const RESP_STYLES = {
  awaiting: 'bg-slate-200 text-slate-700',
  not_requested: 'bg-slate-100 text-slate-500',
  accepted: 'bg-emerald-100 text-emerald-800',
  parsed: 'bg-emerald-100 text-emerald-800',
  received: 'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-800',
};
const QUAL_STYLES = {
  pass: 'bg-emerald-200 text-emerald-900',
  fail: 'bg-red-100 text-red-800',
  pending: 'bg-amber-100 text-amber-800',
};
const ROUND_STATE_STYLES = {
  AWAITING_RESPONSE: 'bg-indigo-100 text-indigo-800',
  TA1_RECEIVED: 'bg-blue-100 text-blue-800',
  '999_RECEIVED': 'bg-blue-100 text-blue-800',
  '271_RECEIVED': 'bg-emerald-100 text-emerald-800',
  COMPLETE: 'bg-emerald-200 text-emerald-900',
  REJECTED: 'bg-red-100 text-red-800',
  REQUIRES_CORRECTION: 'bg-red-100 text-red-800',
  READY_FOR_PREFLIGHT: 'bg-blue-100 text-blue-800',
  AWAITING_CONFIRMATION: 'bg-amber-100 text-amber-800',
  NOT_READY: 'bg-slate-200 text-slate-700',
  NOT_PREPARED: 'bg-slate-100 text-slate-600',
  ALREADY_SUBMITTED: 'bg-slate-200 text-slate-700',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' }) : '—');

function ResponseLine({ label, resp, testId }) {
  const status = resp?.status || 'awaiting';
  return (
    <div className="flex items-center gap-2 py-0.5" data-testid={testId}>
      <span className="text-[11px] font-medium text-slate-600 w-8">{label}</span>
      <Badge className={`${RESP_STYLES[status] || 'bg-slate-200'} text-[10px]`} data-testid={`${testId}-status`} title={resp?.detail || ''}>
        {status === 'not_requested' ? 'not requested' : status}
      </Badge>
      {status === 'not_requested' && (
        <span className="text-[10px] text-slate-500" data-testid={`${testId}-detail`}>ISA14=0 · not required for qualification</span>
      )}
      {resp?.filename && (
        <span className="text-[10px] text-slate-500 truncate" title={resp.filename}>{fmt(resp.processed_at)}</span>
      )}
    </div>
  );
}

function RoundCard({ item }) {
  const inbound = item.inbound || {};
  return (
    <div className="rounded-md border bg-white p-3 text-xs space-y-1" data-testid={`cert-round-${item.round}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-slate-800">Round {item.round} — {fmtDate(item.date)}</p>
        <Badge className={ROUND_STATE_STYLES[item.state] || 'bg-slate-200'} data-testid={`cert-round-${item.round}-state`}>
          {(item.state || 'UNKNOWN').replace(/_/g, ' ')}
        </Badge>
      </div>
      <p className="text-slate-600" data-testid={`cert-round-${item.round}-inquiries`}>{item.inquiry_count} inquiries · Sent {fmt(item.sent_at)}</p>
      <p className="text-slate-500">ISA13 {item.isa13} · GS06 {item.gs06} · ST02 {item.st02}</p>
      <ResponseLine label="TA1" resp={inbound.ta1} testId={`cert-round-${item.round}-ta1`} />
      <ResponseLine label="999" resp={inbound.x999} testId={`cert-round-${item.round}-999`} />
      <ResponseLine label="271" resp={inbound.x271} testId={`cert-round-${item.round}-271`} />
      <div className="flex items-center gap-2 pt-1">
        <span className="text-[11px] text-slate-600">qualification</span>
        <Badge className={QUAL_STYLES[item.qualification] || 'bg-slate-200'} data-testid={`cert-round-${item.round}-qualification`}>
          {item.qualification}
        </Badge>
        {item.qualification_reason && <span className="text-[10px] text-slate-500">{item.qualification_reason}</span>}
      </div>
    </div>
  );
}

function NextRoundCard({ next }) {
  if (!next || !next.round) return null;
  return (
    <div className="rounded-md border border-dashed bg-slate-50 p-3 text-xs space-y-1" data-testid={`cert-round-${next.round}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-slate-800">Round {next.round}</p>
        <Badge className={ROUND_STATE_STYLES[next.state] || 'bg-slate-200'} data-testid={`cert-round-${next.round}-state`}>
          {(next.state || 'NOT_PREPARED').replace(/_/g, ' ')}
        </Badge>
      </div>
      {next.staged_file
        ? <p className="text-slate-600">Staged: {next.staged_file} · sha {next.staged_sha256_12}</p>
        : <p className="text-slate-500">Requires a fresh upload → review → attestation → stage for this round.</p>}
      {next.reason && <p className="text-slate-500">{next.reason}</p>}
      {next.blocked_by?.length > 0 && (
        <p className="text-amber-700" data-testid={`cert-round-${next.round}-blocked`}>
          Transmission blocked today by: {next.blocked_by.join(', ')}
        </p>
      )}
    </div>
  );
}

export const CertRoundsView = ({ rounds, inbound }) => {
  if (!rounds) return null;
  const poller = inbound?.poller || {};
  const items = rounds.items || [];
  const nextRound = rounds.next?.round || null;
  const firstPlaceholder = nextRound ? nextRound + 1 : items.length + 1;
  const placeholders = [];
  for (let r = firstPlaceholder; r <= rounds.required; r += 1) placeholders.push(r);
  return (
    <div className="space-y-3" data-testid="cert-rounds-view">
      <div className="grid sm:grid-cols-3 gap-2 text-xs" data-testid="cert-rounds-counters">
        <div className="rounded-md border bg-slate-50 p-2">
          <p className="text-slate-500">Submitted</p>
          <p className="text-lg font-semibold" data-testid="cert-rounds-submitted">{rounds.submitted} of {rounds.required}</p>
        </div>
        <div className="rounded-md border bg-slate-50 p-2">
          <p className="text-slate-500">Awaiting responses</p>
          <p className="text-lg font-semibold" data-testid="cert-rounds-awaiting">{rounds.awaiting}</p>
        </div>
        <div className="rounded-md border bg-slate-50 p-2">
          <p className="text-slate-500">Qualifying dates completed</p>
          <p className="text-lg font-semibold" data-testid="cert-rounds-completed">{rounds.completed} of {rounds.required}</p>
        </div>
      </div>
      <p className="text-[11px] text-slate-500" data-testid="cert-poller-state">
        Inbound poller: {poller.enabled === false ? 'disabled' : (poller.status || 'not yet run')}
        {poller.mode ? ` · ${poller.mode}` : ''}{poller.environment ? ` · ${poller.environment}` : ''}
        {' · last successful CERT poll '}{fmt(poller.last_successful_poll_at)}
        {poller.skipped_reason ? ` · refused: ${poller.skipped_reason}` : ''}
      </p>
      <div className="grid md:grid-cols-3 gap-3">
        {items.map((it) => <RoundCard key={it.submission_id} item={it} />)}
        <NextRoundCard next={rounds.next} />
        {placeholders.map((r) => (
          <div key={r} className="rounded-md border border-dashed bg-slate-50 p-3 text-xs text-slate-400" data-testid={`cert-round-${r}`}>
            Round {r} — not prepared
          </div>
        ))}
      </div>
      {inbound?.quarantined_271_count > 0 && (
        <p className="text-xs text-amber-700" data-testid="cert-quarantined-271">
          {inbound.quarantined_271_count} uncorrelated 271 file(s) quarantined for operator review
        </p>
      )}
    </div>
  );
};
